import { DependencyList, useEffect } from 'react';

/**
 * @summary Attaches an event listener to the global window.
 *
 * Attaches an event listener to the global window. The listener will be automatically removed when
 * the consuming component is unloaded.
 *
 * @param {string} type
 * @param {Function} listener
 * @param {AddEventListenerOptions} [options]
 * @param {DependencyList} [deps]
 *
 * @example
 * useWindowListener('keydown', (e) => {
 *   // do something if a key was pressed
 * });
 */
export function useWindowListener<K extends keyof WindowEventMap>(
  type: K,
  listener: (this: Window, ev: WindowEventMap[K]) => void,
  options?: AddEventListenerOptions,
  deps?: DependencyList,
): void {
  return useEffect(() => {
    if (!window) return undefined;
    window.addEventListener(type, listener, options);
    return () => window.removeEventListener(type, listener, options);
  }, [listener, options, type, deps]);
}
